import type { FieldType, IEventIndex } from '@/interfaces'

export class EventIndex implements IEventIndex {
	[key: string]: FieldType
	id: string | number
	code: string
	name: string
	startDate: Date
	endDate: Date

	// Basic info
	alias: string | null
	type: number
	typeName?: string // Display name for the event type
	parentId: number | null

	// Audit fields
	createDate: Date
	createBy?: string
	updateDate?: Date
	updateBy?: string

	// Location details
	facility: string
	address1: string | null
	address2: string | null
	address3: string | null
	city: string
	state: string
	zip: string
	country: string

	// Map coordinates
	placeId: string | null
	latitude: number
	longitude: number
	mapBoundsNorth: number
	mapBoundsSouth: number
	mapBoundsEast: number
	mapBoundsWest: number
	mapOverride: boolean

	// Job relationship
	jobCount: number

	constructor({
		id,
		code,
		name,
		startDate,
		endDate,
		alias,
		type,
		typeName,
		parentId,
		createDate,
		createBy,
		updateDate,
		updateBy,
		facility,
		address1,
		address2,
		address3,
		city,
		state,
		zip,
		country,
		placeId,
		latitude,
		longitude,
		mapBoundsNorth,
		mapBoundsSouth,
		mapBoundsEast,
		mapBoundsWest,
		mapOverride,
		jobCount
	}: IEventIndex) {
		this.id = id
		this.code = code
		this.name = name
		this.startDate = startDate ?? new Date()
		// If endDate is null/undefined, use startDate
		this.endDate = endDate ?? this.startDate

		this.alias = alias ?? null
		this.type = type ?? 0
		this.typeName = typeName
		this.parentId = parentId ?? null

		this.createDate = createDate ?? new Date()
		this.createBy = createBy
		this.updateDate = updateDate
		this.updateBy = updateBy

		this.facility = facility ?? ''
		this.address1 = address1 ?? null
		this.address2 = address2 ?? null
		this.address3 = address3 ?? null
		this.city = city ?? ''
		this.state = state ?? ''
		this.zip = zip ?? ''
		this.country = country ?? ''

		this.placeId = placeId ?? null
		this.latitude = latitude ?? 0
		this.longitude = longitude ?? 0
		this.mapBoundsNorth = mapBoundsNorth ?? 0
		this.mapBoundsSouth = mapBoundsSouth ?? 0
		this.mapBoundsEast = mapBoundsEast ?? 0
		this.mapBoundsWest = mapBoundsWest ?? 0
		this.mapOverride = mapOverride ?? false

		this.jobCount = jobCount ?? 0
	}

	// Check if event has valid map coordinates for display
	get hasMapCoordinates(): boolean {
		return this.latitude !== 0 && this.longitude !== 0
	}

	// Formatted date range for display
	get dateRange(): string {
		const start = new Date(this.startDate).toLocaleDateString()
		const end = new Date(this.endDate).toLocaleDateString()

		// If start and end dates are the same, only show one date
		if (start === end) {
			return start
		}

		return `${start} - ${end}`
	}

	// Check if event was created in the past 7 days (including today)
	get isNewShow(): boolean {
		const now = new Date()
		const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
		const created = new Date(this.createDate)
		return created >= sevenDaysAgo && created <= now
	}
}
